import React, { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Trash2, Edit3, Calendar as CalendarIcon, Copy, ExternalLink, UserPlus, Link2, Globe, ThumbsUp, ThumbsDown } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { format } from "date-fns";

const STATUS_COLORS = {
  draft: "bg-surface-2 text-bone/80",
  pending: "bg-yellow-500/20 text-yellow-300 border border-yellow-500/40",
  approved: "bg-klein text-white",
  published: "bg-emerald-500/20 text-emerald-300 border border-emerald-500/40",
  scheduled: "bg-safety/20 text-safety border border-safety/40",
};

export default function ArticleList({ articles, onEdit, onChanged, linkedinUrl }) {
  const [feedback, setFeedback] = useState({});

  React.useEffect(() => {
    let cancelled = false;
    api.get("/recommendations/feedback/me").then(({ data }) => {
      if (!cancelled) setFeedback(data || {});
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const handleFeedback = async (articleId, current, next) => {
    const signal = current === next ? "clear" : next;
    try {
      await api.post("/recommendations/feedback", { article_id: articleId, signal });
      setFeedback((f) => {
        const copy = { ...f };
        if (signal === "clear") delete copy[articleId];
        else copy[articleId] = signal;
        return copy;
      });
    } catch {
      toast.error("Couldn't save feedback");
    }
  };

  if (!articles || articles.length === 0) {
    return (
      <div className="border border-hairline p-12 text-center" data-testid="articles-empty-state">
        <div className="label-mono text-bone/40">no articles yet</div>
        <p className="mt-3 text-bone/60">
          Drag the orange Lingent icon or click <span className="text-bone">New Article</span> to start.
        </p>
      </div>
    );
  }

  return (
    <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="article-list">
      {articles.map((a) => (
        <Card key={a.id} a={a} onEdit={onEdit} onChanged={onChanged} linkedinUrl={linkedinUrl}
              feedback={feedback[a.id]} onFeedback={(next) => handleFeedback(a.id, feedback[a.id], next)} />
      ))}
    </div>
  );
}

function Card({ a, onEdit, onChanged, linkedinUrl, feedback, onFeedback }) {
  const [busy, setBusy] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [inviteLink, setInviteLink] = useState("");
  const [inviting, setInviting] = useState(false);

  const invite = async () => {
    setInviting(true);
    try {
      const body = {};
      if (inviteEmail.trim()) body.email = inviteEmail.trim();
      if (inviteName.trim()) body.name = inviteName.trim();
      const { data } = await api.post(`/articles/${a.id}/reviewers`, body);
      setInviteLink(data.review_url);
      toast.success(data.emailed ? "Invite emailed + link generated" : "Review link generated");
      onChanged?.();
    } catch (e) {
      toast.error("Invite failed");
    } finally {
      setInviting(false);
    }
  };

  const copyInviteLink = async () => {
    try { await navigator.clipboard.writeText(inviteLink); toast.success("Link copied"); } catch { toast.message("Copy blocked"); }
  };

  const del = async () => {
    if (!window.confirm("Delete this article?")) return;
    setBusy(true);
    try {
      await api.delete(`/articles/${a.id}`);
      toast.success("Deleted");
      onChanged?.();
    } catch {
      toast.error("Delete failed");
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    const text = `${a.title}\n\n${a.body}\n\n${(a.hashtags || []).join(" ")}`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard");
    } catch {
      toast.message("Copy blocked by browser");
    }
  };

  const schedule = async (date) => {
    if (!date) return;
    setBusy(true);
    try {
      await api.patch(`/articles/${a.id}`, {
        status: "scheduled",
        scheduled_for: date.toISOString(),
      });
      toast.success(`Scheduled for ${format(date, "PPP")}`);
      onChanged?.();
    } catch {
      toast.error("Schedule failed");
    } finally {
      setBusy(false);
    }
  };

  const markPublished = async () => {
    setBusy(true);
    try {
      await api.patch(`/articles/${a.id}`, { status: "published" });
      toast.success("Marked as published");
      onChanged?.();
    } catch {
      toast.error("Update failed");
    } finally {
      setBusy(false);
    }
  };

  const createLandingPage = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/landing-pages", { article_id: a.id });
      const fullUrl = `${window.location.origin}/p/${data.slug}?utm_source=linkedin&utm_medium=social&utm_campaign=${encodeURIComponent(a.category || "general")}`;
      try {
        await navigator.clipboard.writeText(fullUrl);
        toast.success("Landing page created — link copied");
      } catch {
        toast.success(`Landing page: ${fullUrl}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create landing page");
    } finally { setBusy(false); }
  };

  return (
    <div className="border border-hairline bg-surface hover:bg-surface-2 transition-colors group flex flex-col" data-testid={`article-card-${a.id}`}>
      {a.cover_image ? (
        <div className="aspect-[16/9] overflow-hidden border-b border-hairline">
          <img src={a.cover_image} alt="" className="w-full h-full object-cover" />
        </div>
      ) : (
        <div className="aspect-[16/9] grid-bg border-b border-hairline relative">
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="font-display text-3xl font-black text-bone/15 tracking-tightest">
              {a.category}
            </span>
          </div>
        </div>
      )}
      <div className="p-5 flex-1 flex flex-col">
        <div className="flex items-center justify-between gap-2 mb-3">
          <span className={`label-mono px-2 py-0.5 ${STATUS_COLORS[a.status] || ""}`} data-testid={`article-status-${a.id}`}>
            {a.status}
          </span>
          <span className="label-mono text-[10px] text-bone/40">{a.category}</span>
        </div>
        <h3 className="font-display text-lg font-bold tracking-tightest leading-tight line-clamp-2" data-testid={`article-title-${a.id}`}>
          {a.title}
        </h3>
        <BulletSummary body={a.body} testid={`article-summary-${a.id}`} />
        {a.scheduled_for && (
          <div className="mt-3 label-mono text-[10px] text-safety">
            scheduled · {format(new Date(a.scheduled_for), "PPP")}
          </div>
        )}
        <div className="mt-auto pt-4 flex flex-wrap gap-2 border-t border-hairline">
          <div className="flex gap-0.5 mr-1" data-testid={`feedback-${a.id}`}>
            <button
              onClick={() => onFeedback("like")}
              data-testid={`like-btn-${a.id}`}
              aria-label="Useful"
              className={`p-1.5 border ${feedback === "like" ? "border-emerald-500 bg-emerald-500/15 text-emerald-300" : "border-hairline text-bone/50 hover:text-emerald-300 hover:border-emerald-700/40"}`}
            >
              <ThumbsUp className="w-3 h-3" />
            </button>
            <button
              onClick={() => onFeedback("dislike")}
              data-testid={`dislike-btn-${a.id}`}
              aria-label="Not useful"
              className={`p-1.5 border ${feedback === "dislike" ? "border-red-500 bg-red-500/15 text-red-300" : "border-hairline text-bone/50 hover:text-red-300 hover:border-red-700/40"}`}
            >
              <ThumbsDown className="w-3 h-3" />
            </button>
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onEdit(a)}
            disabled={busy}
            data-testid={`edit-btn-${a.id}`}
            className="rounded-none label-mono hover:bg-bone hover:text-ink h-8 px-2"
          >
            <Edit3 className="w-3 h-3 mr-1" /> Edit
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={copy}
            disabled={busy}
            data-testid={`copy-btn-${a.id}`}
            className="rounded-none label-mono hover:bg-bone hover:text-ink h-8 px-2"
          >
            <Copy className="w-3 h-3 mr-1" /> Copy
          </Button>

          <Popover>
            <PopoverTrigger asChild>
              <Button
                size="sm"
                variant="ghost"
                disabled={busy || a.status === "published"}
                data-testid={`schedule-btn-${a.id}`}
                className="rounded-none label-mono hover:bg-bone hover:text-ink h-8 px-2"
              >
                <CalendarIcon className="w-3 h-3 mr-1" /> Schedule
              </Button>
            </PopoverTrigger>
            <PopoverContent className="rounded-none border-hairline bg-surface p-0" align="start">
              <Calendar
                mode="single"
                onSelect={schedule}
                disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                initialFocus
              />
            </PopoverContent>
          </Popover>

          {a.status !== "published" && (
            <Button
              size="sm"
              onClick={markPublished}
              disabled={busy}
              data-testid={`publish-btn-${a.id}`}
              className="rounded-none label-mono bg-safety hover:bg-safety/90 text-white h-8 px-2"
            >
              <ExternalLink className="w-3 h-3 mr-1" /> Mark Published
            </Button>
          )}

          <Button
            size="sm"
            variant="ghost"
            onClick={() => { setInviteOpen(true); setInviteLink(""); setInviteEmail(""); setInviteName(""); }}
            disabled={busy}
            data-testid={`invite-reviewer-btn-${a.id}`}
            className="rounded-none label-mono hover:bg-bone hover:text-ink h-8 px-2"
          >
            <UserPlus className="w-3 h-3 mr-1" /> Review
          </Button>

          <Button
            size="sm"
            variant="ghost"
            onClick={createLandingPage}
            disabled={busy}
            data-testid={`landing-page-btn-${a.id}`}
            className="rounded-none label-mono hover:bg-bone hover:text-ink h-8 px-2"
          >
            <Globe className="w-3 h-3 mr-1" /> Landing
          </Button>

          <Button
            size="sm"
            variant="ghost"
            onClick={del}
            disabled={busy}
            data-testid={`delete-btn-${a.id}`}
            className="rounded-none label-mono text-red-400 hover:bg-red-500/10 hover:text-red-300 h-8 px-2 ml-auto"
          >
            <Trash2 className="w-3 h-3" />
          </Button>
        </div>
      </div>

      <Dialog open={inviteOpen} onOpenChange={(o) => !o && setInviteOpen(false)}>
        <DialogContent className="rounded-none bg-ink border-hairline text-bone max-w-md" data-testid={`invite-dialog-${a.id}`}>
          <DialogHeader>
            <DialogTitle className="font-display tracking-tightest">Invite a reviewer</DialogTitle>
            <DialogDescription className="text-bone/60 text-sm">
              Stakeholders review without signing up. Email = sends magic link. No email = shareable URL only.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="label-mono text-bone/70">Reviewer name (optional)</Label>
              <Input
                value={inviteName}
                onChange={(e) => setInviteName(e.target.value)}
                placeholder="e.g. CTO of XYZ Plant"
                className="mt-1 rounded-none bg-surface border-hairline focus-visible:ring-1 focus-visible:ring-klein h-10"
                data-testid="invite-name-input"
              />
            </div>
            <div>
              <Label className="label-mono text-bone/70">Reviewer email (optional)</Label>
              <Input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="reviewer@example.com"
                className="mt-1 rounded-none bg-surface border-hairline focus-visible:ring-1 focus-visible:ring-klein h-10"
                data-testid="invite-email-input"
              />
            </div>
            {inviteLink && (
              <div className="border border-hairline bg-surface p-3 text-xs break-all" data-testid="invite-link-display">
                <div className="label-mono text-safety mb-1">// shareable link</div>
                {inviteLink}
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              {inviteLink && (
                <Button
                  variant="outline"
                  onClick={copyInviteLink}
                  data-testid="invite-copy-btn"
                  className="rounded-none border-hairline bg-transparent hover:bg-bone hover:text-ink label-mono"
                >
                  <Link2 className="w-4 h-4 mr-2" /> Copy link
                </Button>
              )}
              <Button
                onClick={invite}
                disabled={inviting}
                data-testid="invite-send-btn"
                className="rounded-none bg-klein hover:bg-klein-hover text-white label-mono"
              >
                {inviting ? "Generating…" : inviteEmail ? "Email + generate link" : "Generate link"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


/** Render body as 2-3 bullet points. If body already contains bullet markers, use them;
 * else split into sentences and take the first 3.
 */
function BulletSummary({ body, testid }) {
  const bullets = React.useMemo(() => {
    const text = (body || "").trim();
    if (!text) return [];
    const lineBullets = text
      .split(/\r?\n/)
      .map((l) => l.trim().replace(/^[\u2022\-\*]\s*/, ""))
      .filter((l) => l.length > 6 && !/^title[:.]/i.test(l) && !/^hashtags?[:.]/i.test(l));
    const native = lineBullets.filter((l) => /^[\u2022\-\*]/.test(text.split("\n").find((tl) => tl.includes(l)) || ""));
    let chosen = native.length >= 2 ? native : lineBullets;
    if (chosen.length < 2) {
      chosen = text.split(/(?<=[.!?])\s+/).map((s) => s.trim()).filter((s) => s.length > 8);
    }
    return chosen.slice(0, 3).map((s) => s.replace(/[\u2022\-\*]+\s*/, "").trim());
  }, [body]);

  if (bullets.length === 0) return null;
  return (
    <ul className="mt-2 space-y-1.5 text-bone/65 text-sm leading-relaxed" data-testid={testid}>
      {bullets.map((b, i) => (
        <li key={i} className="flex gap-2 line-clamp-2">
          <span className="text-safety shrink-0 mt-1.5 w-1 h-1 rounded-full bg-safety" />
          <span>{b}</span>
        </li>
      ))}
    </ul>
  );
}
